/**
 * Synth VR's J (SRVR-0359, SRVR-0360): the desktop editor's `Track.JoinRailsAndNotes` with no area
 * selected and `Track.JoinNotesAndRailsInSelection` with one. Pure functions over the chart's notes;
 * the store resolves the type, the playhead and the range, and writes the result as one undo entry.
 *
 * Times are compared in ms: a head is `time_ms`, a node is `node[2] / 0.02`. Two times are "the same"
 * within `tolMs`, the editor's on-beat tolerance, where the desktop compared floats exactly.
 */
import type { Note, ToolMode } from '../types/beatmap';
import { noteFilterKind, type AreaRange, inRange, type NoteFilterKind } from './areaSelection';
import { toolNoteKind } from './vrTools';

export type Node3 = [number, number, number];

export const JOIN_FROM_LAST_NODE = 'Can only join from last node in rail or single note.';
export const NOTHING_TO_JOIN_FROM = 'Nothing to join from.';
export const NOTHING_TO_JOIN_TO = 'Nothing to join to.';

/** The desktop's `selectedNoteType` before the mapper picks one. */
export const DEFAULT_JOIN_KIND: NoteFilterKind = 'right_hand';

/** The type J joins: the current tool's, else the last note or rail tool's (SRVR-0359 AC2). */
export function joinKind(mode: ToolMode, lastKind: NoteFilterKind | null): NoteFilterKind {
  return toolNoteKind(mode) ?? lastKind ?? DEFAULT_JOIN_KIND;
}

export type JoinResult = { notes: Note[] } | { refused: string | null };

export const nodeMs = (n: readonly number[]) => n[2] / 0.02;
export const isRail = (n: Note) => !!n.rail_nodes && n.rail_nodes.length > 0;
export const sortedNodes = (n: Note): Node3[] =>
  (n.rail_nodes ?? []).map(p => [p[0], p[1], p[2]] as Node3).sort((a, b) => a[2] - b[2]);
export const headNode = (n: Note): Node3 => [n.position[0], n.position[1], n.time_ms * 0.02];
/** A single note's end is its own time; a rail's is its last node's (`GetEndNodeZPosFromRailInUnits`). */
const endMs = (n: Note) => (isRail(n) ? nodeMs(sortedNodes(n)[n.rail_nodes!.length - 1]) : n.time_ms);

/** The joined rail: the first piece keeps its time, position and type; the other pieces go. */
function _replace(notes: readonly Note[], first: number, nodes: Node3[], gone: Set<number>): Note[] {
  return notes
    .map((n, i) => (i === first ? { ...n, has_rail: true, rail_nodes: nodes } : n))
    .filter((_, i) => !gone.has(i));
}

/** `JoinRailsAndNotes`: join the piece ending on the playhead to the next one of `kind`. */
export function playheadJoin(notes: readonly Note[], playheadMs: number, tolMs: number, kind: NoteFilterKind): JoinResult {
  const on = (ms: number) => Math.abs(ms - playheadMs) < tolMs;
  const ofKind = (n: Note) => noteFilterKind(n) === kind;

  // A single note of the type on the playhead comes first; the last one when there are several.
  let from = -1;
  notes.forEach((n, i) => { if (ofKind(n) && !isRail(n) && on(n.time_ms)) from = i; });

  let to = -1;
  if (from < 0) {
    const railsHere = notes.some(n => isRail(n) && (on(n.time_ms) || n.rail_nodes!.some(p => on(nodeMs(p)))));
    if (!railsHere) return { refused: NOTHING_TO_JOIN_FROM };
    for (let i = 0; i < notes.length; i++) {
      const n = notes[i];
      if (!ofKind(n) || !isRail(n)) continue;
      if (on(n.time_ms)) to = i;   // a rail of the type starting on the playhead: joined to in place
      const nodes = sortedNodes(n);
      if (nodes.some(p => on(nodeMs(p)))) {
        if (!on(nodeMs(nodes[nodes.length - 1]))) return { refused: JOIN_FROM_LAST_NODE };
        from = i;
      }
    }
    if (from < 0) return { refused: JOIN_FROM_LAST_NODE };
  }
  // Only a rail joined from can meet a rail starting where it ends.
  const concurrent = to >= 0 && to !== from && isRail(notes[from]);
  if (!concurrent) {
    // The next note or rail of the type ahead of the playhead (`GetNextNoteObject`), the last at its time.
    const ahead = notes.map((_, i) => i).filter(i => ofKind(notes[i]) && notes[i].time_ms > playheadMs && !on(notes[i].time_ms));
    if (!ahead.length) return { refused: NOTHING_TO_JOIN_TO };
    const nextMs = Math.min(...ahead.map(i => notes[i].time_ms));
    to = ahead.filter(i => Math.abs(notes[i].time_ms - nextMs) < tolMs).pop()!;
  }

  const target = notes[to];
  const nodes = sortedNodes(notes[from]);
  // The desktop changed this "due to popular demand": the head joined to wins over the end node.
  if (concurrent) nodes[nodes.length - 1] = headNode(target);
  else nodes.push(headNode(target));
  nodes.push(...sortedNodes(target));
  return { notes: _replace(notes, from, nodes, new Set([to])) };
}

/** `JoinNotesAndRailsInSelection`: join every note and rail of `kind` in the range into one rail. */
export function areaJoin(notes: readonly Note[], range: AreaRange, tolMs: number, kind: NoteFilterKind): JoinResult {
  const heads = notes
    .map((n, i) => ({ n, i }))
    .filter(({ n }) => noteFilterKind(n) === kind && inRange(n.time_beats, range))
    .sort((a, b) => a.n.time_ms - b.n.time_ms || a.i - b.i);

  const pieces: number[] = [];
  let skipBefore: number | null = null;
  for (const { n, i } of heads) {
    if (skipBefore !== null) {
      if (n.time_ms < skipBefore - tolMs) continue;   // inside a piece already taken
      if (!isRail(n) && Math.abs(n.time_ms - skipBefore) < tolMs) continue;   // a single note on its end
    }
    pieces.push(i);
    skipBefore = endMs(n);
  }
  if (pieces.length < 2) return { refused: null };

  const points: Node3[] = pieces.flatMap(i => [headNode(notes[i]), ...sortedNodes(notes[i])]).slice(1);
  // Walk backwards so that of two neighbours at one time the later one — the next rail's head — stays.
  const kept: Node3[] = [];
  let lastMs: number | null = null;
  for (let t = points.length - 1; t >= 0; t--) {
    const ms = nodeMs(points[t]);
    if (lastMs !== null && Math.abs(ms - lastMs) < tolMs) continue;
    lastMs = ms;
    kept.push(points[t]);
  }
  kept.sort((a, b) => a[2] - b[2]);
  return { notes: _replace(notes, pieces[0], kept, new Set(pieces.slice(1))) };
}
